import React, { useState } from 'react'
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Loader2, AlertCircle } from 'lucide-react'
import { ApiService } from '@/services/api'
import type { Restaurant } from '@/types'
import { COUNTRIES, DEFAULT_COUNTRY, getCurrencyForCountry } from '@/lib/countries'
import { getCurrencySymbol } from '@/lib/utils'

interface EditRestaurantFormProps {
  restaurant: Restaurant | null
  open: boolean
  onClose: () => void
  onUpdate: (updatedRestaurant: Restaurant) => void
}

export default function EditRestaurantForm({ restaurant, open, onClose, onUpdate }: EditRestaurantFormProps) {
  const [formData, setFormData] = useState<Partial<Restaurant>>({})
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const isIndia = (formData.country || DEFAULT_COUNTRY) === DEFAULT_COUNTRY
  const currencyCode = getCurrencyForCountry(formData.country || DEFAULT_COUNTRY)
  const currencySymbol = getCurrencySymbol(currencyCode)

  React.useEffect(() => {
    if (restaurant) {
      setFormData({
        name: restaurant.name,
        ownerName: restaurant.ownerName,
        email: restaurant.email,
        phone: restaurant.phone,
        country: restaurant.country || DEFAULT_COUNTRY,
        address: { ...restaurant.address },
        gstin: restaurant.gstin,
        subscriptionStatus: restaurant.subscriptionStatus,
        planType: restaurant.planType,
        paymentStatus: restaurant.paymentStatus,
        subscriptionAmount: restaurant.subscriptionAmount || 12000,
        adminNotes: restaurant.adminNotes || '',
        isActive: restaurant.isActive
      })
      setError(null)
    }
  }, [restaurant])

  const handleInputChange = (field: string, value: any) => {
    if (field.startsWith('address.')) {
      const addressField = field.split('.')[1]
      setFormData(prev => ({
        ...prev,
        address: {
          line1: prev.address?.line1 || '',
          line2: prev.address?.line2 || '',
          city: prev.address?.city || '',
          state: prev.address?.state || '',
          pincode: prev.address?.pincode || '',
          ...prev.address,
          [addressField]: value
        }
      }))
    } else {
      setFormData(prev => ({
        ...prev,
        [field]: value
      }))
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!restaurant) return

    setIsLoading(true)
    setError(null)

    try {
      const response = await ApiService.updateRestaurant(restaurant._id, formData)
      if (response.success) {
        onUpdate(response.data)
        onClose()
      } else {
        setError(response.message || 'Failed to update restaurant')
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to update restaurant')
    } finally {
      setIsLoading(false)
    }
  }

  const handleClose = () => {
    setError(null)
    onClose()
  }

  if (!restaurant) return null

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Edit Restaurant</DialogTitle>
          <DialogDescription>
            Update restaurant information and settings
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-6">
          {error && (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          {/* Basic Information */}
          <div className="space-y-4">
            <h3 className="text-lg font-medium">Basic Information</h3>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="name">Restaurant Name</Label>
                <Input
                  id="name"
                  value={formData.name || ''}
                  onChange={(e) => handleInputChange('name', e.target.value)}
                  required
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="ownerName">Owner Name</Label>
                <Input
                  id="ownerName"
                  value={formData.ownerName || ''}
                  onChange={(e) => handleInputChange('ownerName', e.target.value)}
                  required
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  value={formData.email || ''}
                  onChange={(e) => handleInputChange('email', e.target.value)}
                  required
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="phone">Phone</Label>
                <Input
                  id="phone"
                  value={formData.phone || ''}
                  onChange={(e) => handleInputChange('phone', e.target.value)}
                  required
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="country">Country</Label>
                <Select
                  value={formData.country || DEFAULT_COUNTRY}
                  onValueChange={(value) => handleInputChange('country', value)}
                >
                  <SelectTrigger id="country">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {COUNTRIES.map((c) => (
                      <SelectItem key={c.name} value={c.name}>{c.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>Currency</Label>
                <div className="flex h-9 items-center rounded-md border border-input bg-muted px-3 text-sm text-muted-foreground">
                  {currencyCode} ({currencySymbol})
                </div>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="gstin">{isIndia ? 'GSTIN' : 'Tax ID / VAT Number (optional)'}</Label>
              <Input
                id="gstin"
                value={formData.gstin || ''}
                onChange={(e) => handleInputChange('gstin', e.target.value)}
                required={isIndia}
              />
            </div>
          </div>

          {/* Address */}
          <div className="space-y-4">
            <h3 className="text-lg font-medium">Address</h3>
            
            <div className="space-y-2">
              <Label htmlFor="line1">Address Line 1</Label>
              <Input
                id="line1"
                value={formData.address?.line1 || ''}
                onChange={(e) => handleInputChange('address.line1', e.target.value)}
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="line2">Address Line 2</Label>
              <Input
                id="line2"
                value={formData.address?.line2 || ''}
                onChange={(e) => handleInputChange('address.line2', e.target.value)}
              />
            </div>

            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label htmlFor="city">City</Label>
                <Input
                  id="city"
                  value={formData.address?.city || ''}
                  onChange={(e) => handleInputChange('address.city', e.target.value)}
                  required
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="state">State</Label>
                <Input
                  id="state"
                  value={formData.address?.state || ''}
                  onChange={(e) => handleInputChange('address.state', e.target.value)}
                  required
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="pincode">{isIndia ? 'Pincode' : 'Postal Code'}</Label>
                <Input
                  id="pincode"
                  value={formData.address?.pincode || ''}
                  onChange={(e) => handleInputChange('address.pincode', e.target.value)}
                  required
                />
              </div>
            </div>
          </div>

          {/* Subscription Details */}
          <div className="space-y-4">
            <h3 className="text-lg font-medium">Subscription & Status</h3>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="subscriptionStatus">Subscription Status</Label>
                <Select
                  value={formData.subscriptionStatus}
                  onValueChange={(value) => handleInputChange('subscriptionStatus', value)}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="active">Active</SelectItem>
                    <SelectItem value="expired">Expired</SelectItem>
                    <SelectItem value="suspended">Suspended</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="paymentStatus">Payment Status</Label>
                <Select
                  value={formData.paymentStatus}
                  onValueChange={(value) => handleInputChange('paymentStatus', value)}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="paid">Paid</SelectItem>
                    <SelectItem value="pending">Pending</SelectItem>
                    <SelectItem value="failed">Failed</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label htmlFor="planType">Plan Type</Label>
                <Select
                  value={formData.planType}
                  onValueChange={(value) => handleInputChange('planType', value)}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="yearly">Yearly</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="subscriptionAmount">Subscription Amount (₹)</Label>
                <Input
                  id="subscriptionAmount"
                  type="number"
                  min="0"
                  value={formData.subscriptionAmount || 12000}
                  onChange={(e) => handleInputChange('subscriptionAmount', Number(e.target.value))}
                  placeholder="12000"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="isActive">Restaurant Status</Label>
                <Select
                  value={formData.isActive ? 'active' : 'inactive'}
                  onValueChange={(value) => handleInputChange('isActive', value === 'active')}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="active">Active</SelectItem>
                    <SelectItem value="inactive">Inactive</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>

          {/* Admin Notes */}
          <div className="space-y-4">
            <h3 className="text-lg font-medium">Admin Notes</h3>
            
            <div className="space-y-2">
              <Label htmlFor="adminNotes">Notes</Label>
              <Textarea
                id="adminNotes"
                value={formData.adminNotes || ''}
                onChange={(e) => handleInputChange('adminNotes', e.target.value)}
                placeholder="Add any admin notes here..."
                rows={3}
              />
            </div>
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={handleClose}
              disabled={isLoading}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={isLoading}>
              {isLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {isLoading ? 'Updating...' : 'Update Restaurant'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}